import { OdataResponseItem } from '../../models/odata.model';
import {
  RecommendationPriority,
  RecommendationStatus,
  RecommendationType
} from '../../models/recommendation.model';
import { TenantSite } from '../../models/tenant.model';
import { AlertLevel } from '../../models/user.model';
import {
  ClientTokens,
  ResponseClientTokens,
  ResponseWebhook,
  ResponseWebhookCondition,
  SystemWebhook,
  UserWebhook,
  WebhookConditionOperator,
  WebhookEntityType,
  WebhookEventType,
  WebhookHeader,
  WebhookWorkflowChange,
  WebhookWorkflowChangeType,
  WebhookWorkflowObjectAddedType
} from '../../models/webhook.model';

export abstract class WebhookAdapter {
  public static adaptUserWebhooks(
    response: OdataResponseItem<ResponseWebhook>[]
  ): UserWebhook[] {
    return response.map(({ obj }) => {
      // Notification types
      const notificationType = {
        email: obj.actions.some(({ type }) => type === 'email'),
        sms: obj.actions.some(({ type }) => type === 'sms')
      };

      // Sites filter
      const sitesCondition = obj.condition.conditions?.find(({ conditions }) =>
        conditions?.some(({ property }) => property === 'siteid')
      );
      const sites: TenantSite[] =
        sitesCondition?.conditions?.map(
          ({ value }) => ({ id: value }) as TenantSite
        ) ?? [];

      if (obj.entitytype === WebhookEntityType.Recommendation) {
        // Priority filter
        const priorityCondition = obj.condition.conditions?.find(
          ({ property }) => property === 'priority'
        );
        const priority = priorityCondition
          ? ([priorityCondition.value] as RecommendationPriority[])
          : null;

        // Type filter
        const typeConditions = obj.condition.conditions?.find(
          ({ conditions }) =>
            conditions?.some((condition) => condition.property === 'type')
        );
        const type =
          (typeConditions?.conditions?.map(
            (type) => type.value
          ) as RecommendationType[]) ?? null;

        // Workflow change filter
        let workflowChange = {} as WebhookWorkflowChange;

        let conditions =
          obj.condition.conditions?.filter(
            (c) => c.operator === WebhookConditionOperator.Changed
          ) ?? [];

        for (const condition of obj.condition.conditions ?? []) {
          if (condition.conditions) {
            conditions = conditions.concat(
              condition.conditions.filter(
                (c) => c.operator === WebhookConditionOperator.Changed
              )
            );
          }
        }

        if (
          conditions.some(
            (c) => c.property === WebhookWorkflowChangeType.StatusChange
          )
        ) {
          const statuses: RecommendationStatus[] = [];
          obj.condition.conditions?.map((condition) => {
            if (condition.conditions) {
              for (const c of condition.conditions) {
                if (c.property === WebhookWorkflowChangeType.StatusChange) {
                  statuses.push(c.value as RecommendationStatus);
                }
              }
            }
          });

          workflowChange = {
            type: WebhookWorkflowChangeType.StatusChange,
            value: statuses
          };
        } else if (
          conditions.some(
            (c) => c.property === WebhookWorkflowChangeType.CostAvoidanceChange
          )
        ) {
          workflowChange = {
            type: WebhookWorkflowChangeType.CostAvoidanceChange,
            value: null
          };
        } else {
          workflowChange = {
            type: WebhookWorkflowChangeType.ObjectAdded,
            value: conditions.map(
              (c) => c.property as WebhookWorkflowObjectAddedType
            )
          };
        }

        return {
          id: obj.id,
          name: obj.name,
          description: obj.description,
          enabled: obj.isactive,
          entityType: obj.entitytype,
          type,
          sites,
          priority,
          workflowChange,
          notificationType
        };
      }

      const levelCondition = obj.condition.conditions?.find(({ conditions }) =>
        conditions?.some(({ property }) => property === 'threshold.criticality')
      );
      const level = levelCondition
        ? levelCondition.conditions?.map((c) => c.value as AlertLevel)
        : null;

      return {
        id: obj.id,
        name: obj.name,
        description: obj.description,
        enabled: obj.isactive,
        entityType: obj.entitytype,
        sites,
        level: level,
        notificationType
      };
    });
  }

  public static adaptSystemWebhooks(
    response: OdataResponseItem<ResponseWebhook>[]
  ): SystemWebhook[] {
    return response.map(({ obj }) => {
      const url = obj.actions.find(({ type }) => type === 'url')?.['url'] ?? '';
      const headers =
        (Object.keys(
          obj.actions.find(({ type }) => type === 'url')?.['headers'] ?? []
        ).map((key) => ({
          key,
          value: obj.actions.find(({ type }) => type === 'url')?.['headers']?.[
            key as any
          ]
        })) as WebhookHeader[]) ?? [];

      // Sites filter
      const sitesCondition = obj.condition?.conditions?.find(({ conditions }) =>
        conditions?.some(({ property }) => property === 'siteid')
      );

      const sites: TenantSite[] =
        sitesCondition?.conditions?.map(
          ({ value }) => ({ id: value }) as TenantSite
        ) ?? [];

      if (obj.entitytype === WebhookEntityType.Recommendation) {
        const statusCondition = obj.condition?.conditions?.find(
          ({ conditions }) =>
            conditions?.some(
              (condition) => condition.value === RecommendationStatus.New
            )
        );

        if (statusCondition) {
          obj.events = [WebhookEventType.Create];
        }
      }

      const webhook: SystemWebhook = {
        id: obj.id,
        name: obj.name,
        description: obj.description,
        enabled: obj.isactive,
        entityType: obj.entitytype,
        sites,
        url,
        headers,
        events: obj.events,
        custom: obj.custom ?? false
      };

      return webhook;
    });
  }

  public static adaptUserWebhookToRequest(
    webhook: UserWebhook
  ): ResponseWebhook {
    // Notification types
    const actions = [];
    if (webhook.notificationType.email) {
      actions.push({ type: 'email' });
    }

    if (webhook.notificationType.sms) {
      actions.push({ type: 'sms' });
    }

    const condition: ResponseWebhookCondition = {
      operator: WebhookConditionOperator.And,
      conditions: []
    };

    // Site filter
    if (webhook.sites && webhook.sites.length) {
      condition.conditions?.push({
        operator: WebhookConditionOperator.Or,
        conditions: webhook.sites.map(({ id }) => ({
          operator: WebhookConditionOperator.Equals,
          property: 'siteid',
          value: id
        }))
      });
    }

    if (webhook.entityType === 'recommendation') {
      // Priority filter
      if (webhook.priority && webhook.priority.length === 1) {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Equals,
          property: 'priority',
          value: webhook.priority[0]
        });
      }

      // Type filter
      if (webhook.type && webhook.type.length > 0) {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: (webhook.type as RecommendationType[]).map((type) => ({
            operator: WebhookConditionOperator.Equals,
            property: 'type',
            value: type
          }))
        });
      } else {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: Object.values(RecommendationType)
            .filter((t) => t !== RecommendationType.Reference)
            .map((type) => ({
              operator: WebhookConditionOperator.Equals,
              property: 'type',
              value: type
            }))
        });
      }

      if (
        webhook.workflowChange?.type === WebhookWorkflowChangeType.StatusChange
      ) {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Changed,
          property: webhook.workflowChange?.type
        });

        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: (
            webhook.workflowChange?.value as RecommendationStatus[]
          ).map((status) => ({
            operator: WebhookConditionOperator.Equals,
            property: WebhookWorkflowChangeType.StatusChange,
            value: status
          }))
        });
      }

      if (
        webhook.workflowChange?.type === WebhookWorkflowChangeType.ObjectAdded
      ) {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: (
            webhook.workflowChange?.value as WebhookWorkflowObjectAddedType[]
          ).map((objectType) => ({
            operator: WebhookConditionOperator.Changed,
            property: objectType
          }))
        });

        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: Object.values(RecommendationStatus)
            .filter((status) => status !== RecommendationStatus.Draft)
            .map((status) => ({
              operator: WebhookConditionOperator.Equals,
              property: WebhookWorkflowChangeType.StatusChange,
              value: status
            }))
        });
      }

      if (
        webhook.workflowChange?.type ===
        WebhookWorkflowChangeType.CostAvoidanceChange
      ) {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: [
            {
              operator: WebhookConditionOperator.Changed,
              property: WebhookWorkflowChangeType.CostAvoidanceChange
            }
          ]
        });

        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: Object.values(RecommendationStatus)
            .filter((status) => status !== RecommendationStatus.Draft)
            .map((status) => ({
              operator: WebhookConditionOperator.Equals,
              property: WebhookWorkflowChangeType.StatusChange,
              value: status
            }))
        });
      }
    } else if (webhook.entityType === 'alert') {
      // Level filter
      condition.conditions?.push({
        operator: WebhookConditionOperator.Or,
        conditions:
          webhook.level?.map((level) => ({
            operator: WebhookConditionOperator.Equals,
            property: 'threshold.criticality',
            value: level
          })) ?? []
      });
    }

    const request: Partial<ResponseWebhook> = {
      name: webhook.name,
      description:
        webhook.entityType === 'recommendation'
          ? $localize`Recommendation Webhook created via User Account Settings`
          : $localize`Alert Webhook created via User Account Settings`,
      entitytype: webhook.entityType,
      isactive: true,
      events:
        webhook.entityType === 'recommendation'
          ? [WebhookEventType.Update]
          : [WebhookEventType.Create],
      actions,
      condition
    };

    return request as ResponseWebhook;
  }

  public static adaptSystemWebhookToRequest(
    webhook: SystemWebhook
  ): ResponseWebhook {
    const actions = [];
    const action = {
      type: 'url',
      url: webhook.url
    };

    let headers: any;

    if (webhook.headers) {
      headers = {};

      webhook.headers.forEach((header) => {
        if (header.key && header.value) {
          headers[header.key] = header.value;
        }
      });

      if (Object.keys(headers).length > 0) {
        Object.assign(action, { headers: headers as WebhookHeader });
      }
    }

    actions.push(action);

    let hasCondition = false;

    const condition: ResponseWebhookCondition = {
      operator: WebhookConditionOperator.And,
      conditions: []
    };

    if (webhook.sites && webhook.sites.length) {
      hasCondition = true;

      condition.conditions?.push({
        operator: WebhookConditionOperator.Or,
        conditions: webhook.sites.map(({ id }) => ({
          operator: WebhookConditionOperator.Equals,
          property: 'siteid',
          value: id
        }))
      });
    }

    if (
      webhook.entityType === WebhookEntityType.Recommendation ||
      webhook.entityType === WebhookEntityType.EFPIntegrations
    ) {
      hasCondition = true;

      if (webhook.events.includes(WebhookEventType.Create)) {
        condition.conditions?.push({
          operator: WebhookConditionOperator.Changed,
          property: WebhookWorkflowChangeType.StatusChange
        });

        condition.conditions?.push({
          operator: WebhookConditionOperator.Or,
          conditions: [
            {
              operator: WebhookConditionOperator.Equals,
              property: WebhookWorkflowChangeType.StatusChange,
              value: RecommendationStatus.New
            }
          ]
        });

        webhook.events = [WebhookEventType.Update];
      } else if (webhook.events.includes(WebhookEventType.Update)) {
        if (webhook.entityType === WebhookEntityType.Recommendation) {
          condition.conditions?.push({
            operator: WebhookConditionOperator.Changed,
            property: WebhookWorkflowChangeType.StatusChange
          });

          condition.conditions?.push({
            operator: WebhookConditionOperator.Or,
            conditions: Object.values(RecommendationStatus)
              .filter(
                (s) =>
                  s !== RecommendationStatus.Draft &&
                  s !== RecommendationStatus.New
              )
              .map((s) => ({
                operator: WebhookConditionOperator.Equals,
                property: WebhookWorkflowChangeType.StatusChange,
                value: s
              }))
          });
        } else if (webhook.entityType === WebhookEntityType.EFPIntegrations) {
          condition.conditions?.push({
            operator: WebhookConditionOperator.Changed,
            property: WebhookWorkflowChangeType.StatusChange
          });

          condition.conditions?.push({
            operator: WebhookConditionOperator.Or,
            conditions: Object.values(RecommendationStatus)
              .filter(
                (s) =>
                  s !== RecommendationStatus.Draft &&
                  s !== RecommendationStatus.Closed &&
                  s !== RecommendationStatus.CaReview
              )
              .map((s) => ({
                operator: WebhookConditionOperator.Equals,
                property: WebhookWorkflowChangeType.StatusChange,
                value: s
              }))
          });

          webhook.entityType = WebhookEntityType.Recommendation;
        }
      }
    }

    const request: Partial<ResponseWebhook> = {
      name: webhook.name,
      description: webhook.description,
      entitytype: webhook.entityType,
      isactive: true,
      events: webhook.events,
      actions,
      custom: webhook.custom ?? false
    };

    if (hasCondition) {
      Object.assign(request, { condition });
    }

    return request as ResponseWebhook;
  }

  public static adaptClientTokens(
    response: ResponseClientTokens
  ): ClientTokens {
    return {
      clientId: response?.client_id,
      clientSecret: response?.client_secret
    };
  }
}
